import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { FormField } from "@/v2/components/form/form-schema";
import { Text } from "@radix-ui/themes";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  MESSAGE_FILTER_KNOWN_VALUE,
  MessageFilter,
  isMessageFilterKnownValue,
} from "./SystemMessage.model";

const PILL_CLASS =
  "rt-reset rt-BaseButton rt-r-size-2 rt-variant-soft rt-high-contrast rt-Button";

type ValueUpdate = Partial<
  Pick<MessageFilter, "value" | "knownSourceFieldId">
>;

type FilterRuleConditionValueTextOrKnownProps = {
  filter: MessageFilter;
  fieldLabel: string;
  contextFields: FormField[];
  onValueUpdate: (updates: ValueUpdate) => void;
};

export function FilterRuleConditionValueTextOrKnown({
  filter,
  fieldLabel,
  contextFields,
  onValueUpdate,
}: FilterRuleConditionValueTextOrKnownProps) {
  const [localText, setLocalText] = useState(
    isMessageFilterKnownValue(filter.value) ? "" : filter.value
  );
  const hasTextChangedRef = useRef(false);

  useEffect(() => {
    if (isMessageFilterKnownValue(filter.value)) {
      setLocalText("");
    } else {
      setLocalText(filter.value);
    }
    hasTextChangedRef.current = false;
  }, [filter.value]);

  const submitText = useCallback(
    (forceSubmit = false) => {
      if (!forceSubmit && !hasTextChangedRef.current) return;
      onValueUpdate({
        value: localText,
        knownSourceFieldId: undefined,
      });
      hasTextChangedRef.current = false;
    },
    [localText, onValueUpdate]
  );

  const handleTextChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setLocalText(e.target.value);
      hasTextChangedRef.current = true;
    },
    []
  );

  const handlePickKnownField = useCallback(
    (field: FormField) => {
      onValueUpdate({
        value: MESSAGE_FILTER_KNOWN_VALUE,
        knownSourceFieldId: field.id,
      });
    },
    [onValueUpdate]
  );

  const pillContent = useMemo(() => {
    if (isMessageFilterKnownValue(filter.value)) {
      return MESSAGE_FILTER_KNOWN_VALUE;
    }
    return filter.value || "Enter value…";
  }, [filter.value]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div data-accent-color="gray" className={`${PILL_CLASS} shrink`}>
          <Text truncate>{pillContent}</Text>
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[260px]">
        <div className="px-2 py-1">
          <Text size="1" color="gray" weight="medium">
            Specific text
          </Text>
        </div>
        <div className="px-2 pb-2">
          <Input
            placeholder={fieldLabel}
            value={localText}
            onChange={handleTextChange}
            onKeyDown={(e) => {
              if (e.key === "Enter") submitText(true);
            }}
            onBlur={() => submitText(false)}
            className="w-full h-6"
          />
        </div>
        <DropdownMenuSeparator />
        <div className="px-2 py-1">
          <Text size="1" color="gray" weight="medium">
            Compare to context field ({MESSAGE_FILTER_KNOWN_VALUE})
          </Text>
        </div>
        {contextFields.map((field) => (
          <DropdownMenuItem
            key={field.id}
            onClick={() => handlePickKnownField(field)}
          >
            {field.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
